// ...importy
import React, { useEffect, useState } from "react";
import api from "./api";
import { Link, useSearchParams } from "react-router-dom";
import Lightbox from "react-image-lightbox";
import "react-image-lightbox/style.css";

const itemsPerPage = 50;

const ProductTable = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // --- FILTRY I STRONA BEZPOŚREDNIO Z URL ---
  const titleQuery   = searchParams.get("title") || "";
  const modelQuery   = searchParams.get("model") || "";
  const skuQuery     = searchParams.get("sku") || "";
  const dateFrom     = searchParams.get("from") || "";
  const dateTo       = searchParams.get("to") || "";
  const onlyFlagged  = searchParams.get("onlyFlagged") === "1";

  const rawPage = parseInt(searchParams.get("page") || "1", 10);
  const currentPage = Number.isFinite(rawPage) && rawPage > 0 ? rawPage : 1;

  const [products, setProducts] = useState([]);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [lightboxImages, setLightboxImages] = useState([]);

  useEffect(() => {
    api
      .get(`${process.env.REACT_APP_API_URL}/products`, { withCredentials: true })
      .then((res) => setProducts(res.data))
      .catch((err) => console.error("Błąd ładowania produktów", err));
  }, []);

  const norm = (v) => (v ?? "").toString().trim().toLowerCase();

  const filteredProducts = products.filter((p) => {
    const t = norm(titleQuery);
    const m = norm(modelQuery);
    const s = norm(skuQuery);

    const name = norm(p.name);
    const model = norm(p.model);
    const sku = norm(p.catalog_number);

    const okTitle = !t || name.includes(t);
    const okModel = !m || model.includes(m);
    const okSku   = !s || sku.includes(s);

    const createdDate = new Date(p.created_at);
    const from = dateFrom ? new Date(dateFrom) : null;
    const to   = dateTo   ? new Date(`${dateTo}T23:59:59`) : null;
    const okFromTo = (!from || createdDate >= from) && (!to || createdDate <= to);

    const okFlag = !onlyFlagged || p.is_flagged === true;

    return okTitle && okModel && okSku && okFromTo && okFlag;
  });

  const totalPages = Math.max(1, Math.ceil(filteredProducts.length / itemsPerPage));
  const safePage   = Math.min(currentPage, totalPages);

  const paginatedProducts = filteredProducts.slice(
    (safePage - 1) * itemsPerPage,
    safePage * itemsPerPage
  );

  const formatDate = (isoDate) => {
    const d = new Date(isoDate);
    return (
      d.toLocaleDateString("pl-PL") +
      " " +
      d.toLocaleTimeString("pl-PL", { hour: "2-digit", minute: "2-digit" })
    );
  };

  // pomocnicza funkcja do aktualizacji query stringa
  const updateParams = (patch) => {
    const params = new URLSearchParams(searchParams);
    Object.entries(patch).forEach(([key, value]) => {
      if (value === undefined || value === null || value === "") {
        params.delete(key);
      } else {
        params.set(key, String(value));
      }
    });
    setSearchParams(params, { replace: false });
  };

  const clearFilters = () => {
    setSearchParams({}, { replace: false });
  };

  const toggleFlag = async (p) => {
    try {
      const res = await api.patch(
        `${process.env.REACT_APP_API_URL}/products/${p.id}/flag`,
        {},
        { withCredentials: true }
      );
      const is_flagged = res.data?.is_flagged ?? !p.is_flagged;
      setProducts((prev) =>
        prev.map((x) => (x.id === p.id ? { ...x, is_flagged } : x))
      );
    } catch (e) {
      alert("❌ Nie udało się zmienić flagi.");
    }
  };

  // Query do linku detali (zachowujemy aktualny stan filtrów i stronę)
  const buildQueryString = () => {
    return searchParams.toString();
  };

  const renderPageButtons = () => {
    const items = [];
    const RADIUS = 10;

    const pushPage = (page, key = page) => {
      items.push(
        <li key={key} className={`page-item ${safePage === page ? "active" : ""}`}>
          <button
            className="page-link"
            onClick={() => updateParams({ page })}
          >
            {page}
          </button>
        </li>
      );
    };

    if (totalPages <= 2 * RADIUS + 1) {
      for (let p = 1; p <= totalPages; p++) pushPage(p);
      return items;
    }

    let start = Math.max(1, safePage - RADIUS);
    let end = Math.min(totalPages, safePage + RADIUS);

    const need = 2 * RADIUS + 1 - (end - start + 1);
    if (need > 0) {
      if (start === 1) end = Math.min(totalPages, end + need);
      else if (end === totalPages) start = Math.max(1, start - need);
    }

    if (start > 1) {
      pushPage(1, "first");
      if (start > 2) {
        items.push(
          <li key="pre-ellipsis" className="page-item disabled">
            <span className="page-link">…</span>
          </li>
        );
      }
    }

    for (let p = start; p <= end; p++) pushPage(p);

    if (end < totalPages) {
      if (end < totalPages - 1) {
        items.push(
          <li key="post-ellipsis" className="page-item disabled">
            <span className="page-link">…</span>
          </li>
        );
      }
      pushPage(totalPages, "last");
    }

    return items;
  };

  return (
    <div className="container py-4">
      <h2 className="mb-4 text-center">📦 Wycena produktów</h2>

      {/* FILTRY */}
      <div className="row row-cols-1 row-cols-md-6 g-3 mb-4">
        <div className="col">
          <label className="form-label">Nazwa</label>
          <input
            type="text"
            className="form-control"
            placeholder="Szukaj po nazwie…"
            value={titleQuery}
            onChange={(e) => updateParams({ title: e.target.value, page: 1 })}
          />
        </div>
        <div className="col">
          <label className="form-label">Model</label>
          <input
            type="text"
            className="form-control"
            placeholder="Szukaj po modelu…"
            value={modelQuery}
            onChange={(e) => updateParams({ model: e.target.value, page: 1 })}
          />
        </div>
        <div className="col">
          <label className="form-label">Nr katalogowy</label>
          <input
            type="text"
            className="form-control"
            placeholder="Szukaj po numerze kat.…"
            value={skuQuery}
            onChange={(e) => updateParams({ sku: e.target.value, page: 1 })}
          />
        </div>

        <div className="col">
          <label className="form-label">Data od</label>
          <input
            type="date"
            className="form-control"
            value={dateFrom}
            onChange={(e) => updateParams({ from: e.target.value, page: 1 })}
          />
        </div>
        <div className="col">
          <label className="form-label">Data do</label>
          <input
            type="date"
            className="form-control"
            value={dateTo}
            onChange={(e) => updateParams({ to: e.target.value, page: 1 })}
          />
        </div>

        <div className="col d-flex align-items-end">
          <button
            type="button"
            className={`btn w-100 ${onlyFlagged ? "btn-warning" : "btn-outline-warning"}`}
            aria-pressed={onlyFlagged}
            title="Filtruj tylko oflagowane"
            onClick={() =>
              updateParams({
                onlyFlagged: onlyFlagged ? "" : "1",
                page: 1,
              })
            }
          >
            {onlyFlagged ? "⭐ Tylko oflagowane" : "☆ Tylko oflagowane"}
          </button>
        </div>

        <div className="col d-grid">
          <label className="form-label d-none d-md-block">&nbsp;</label>
          <button className="btn btn-secondary" onClick={clearFilters}>
            Wyczyść filtry
          </button>
        </div>
      </div>

      {/* TABELA */}
      <div className="table-responsive">
        <table className="table table-bordered table-striped align-middle text-nowrap small">
          <thead className="table-dark text-center">
            <tr>
              <th>L.p.</th>
              <th>⭐</th>
              <th>Nazwa</th>
              <th>Nr katalogowy</th>
              <th>Model</th>
              <th>St.M</th>
              <th>Ilość</th>
              <th>Jednostka</th>
              <th>Cena(zł)</th>
              <th>Data dodania</th>
              <th>Opis</th>
              <th>Aukcje</th>
              <th>Zdjęcie</th>
            </tr>
          </thead>
          <tbody>
            {paginatedProducts.map((p, index) => (
              <tr key={p.id} className="text-center align-middle">
                <td>{(safePage - 1) * itemsPerPage + index + 1}</td>
                <td>
                  <button
                    className="btn btn-link p-0"
                    title={p.is_flagged ? "Odflaguj" : "Oflaguj"}
                    aria-label={p.is_flagged ? "Odflaguj produkt" : "Oflaguj produkt"}
                    onClick={() => toggleFlag(p)}
                    style={{ textDecoration: "none", fontSize: "1.1rem" }}
                  >
                    {p.is_flagged ? "⭐" : "☆"}
                  </button>
                </td>
                <td className="text-break">
                  <Link to={`/products/${p.id}?${buildQueryString()}`}>
                    {p.name?.length > 30 ? p.name.slice(0, 30) + "(…)" : p.name}
                  </Link>
                </td>
                <td>
                  {p.catalog_number?.length > 15
                    ? p.catalog_number.slice(0, 15) + "(…)"
                    : p.catalog_number}
                </td>
                <td>{p.model}</td>
                <td>{p.location}</td>
                <td>{p.quantity}</td>
                <td>{p.unit}</td>
                <td>
                  {p.price}
                  {p.price_type === "up" && <span className="ms-1 text-success">⬆️</span>}
                  {p.price_type === "down" && <span className="ms-1 text-danger">⬇️</span>}
                  {p.price_type === "constant" && <span className="ms-1 text-muted">➖</span>}
                </td>
                <td>{formatDate(p.created_at)}</td>
                <td>
                  {p.description?.trim() ? (
                    <span className="text-success" title="Opis dostępny">
                      ✅
                    </span>
                  ) : (
                    <span className="text-danger" title="Brak opisu">
                      ❌
                    </span>
                  )}
                </td>
                <td>
                  {p.auction_platform?.includes("allegro") && p.auction_allegro_url && (
                    <div>
                      <a href={p.auction_allegro_url} target="_blank" rel="noreferrer">
                        Allegro
                      </a>
                    </div>
                  )}
                  {p.auction_platform?.includes("olx") && p.auction_olx_url && (
                    <div>
                      <a href={p.auction_olx_url} target="_blank" rel="noreferrer">
                        OLX
                      </a>
                    </div>
                  )}
                </td>
                <td>
                  {p.images?.[0]?.full_url && (
                    <img
                      src={p.images[0].full_url}
                      alt=""
                      className="img-thumbnail"
                      style={{ width: 50, height: 50, cursor: "pointer" }}
                      onClick={() => {
                        setLightboxImages(p.images.map((img) => img.full_url));
                        setLightboxIndex(0);
                        setLightboxOpen(true);
                      }}
                    />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* PAGINACJA */}
      {totalPages > 1 && (
        <div className="mt-4 d-flex justify-content-center">
          <ul className="pagination flex-wrap">
            <li className={`page-item ${safePage === 1 ? "disabled" : ""}`}>
              <button
                className="page-link"
                onClick={() => updateParams({ page: Math.max(1, safePage - 1) })}
              >
                Poprzednia
              </button>
            </li>

            {renderPageButtons()}

            <li className={`page-item ${safePage === totalPages ? "disabled" : ""}`}>
              <button
                className="page-link"
                onClick={() => updateParams({ page: Math.min(totalPages, safePage + 1) })}
              >
                Następna
              </button>
            </li>
          </ul>
        </div>
      )}

      {/* LIGHTBOX */}
      {lightboxOpen && (
        <Lightbox
          mainSrc={lightboxImages[lightboxIndex]}
          nextSrc={lightboxImages[(lightboxIndex + 1) % lightboxImages.length]}
          prevSrc={
            lightboxImages[
              (lightboxIndex + lightboxImages.length - 1) % lightboxImages.length
            ]
          }
          onCloseRequest={() => setLightboxOpen(false)}
          onMovePrevRequest={() =>
            setLightboxIndex(
              (lightboxIndex + lightboxImages.length - 1) % lightboxImages.length
            )
          }
          onMoveNextRequest={() =>
            setLightboxIndex((lightboxIndex + 1) % lightboxImages.length)
          }
          imageTitle={`Zdjęcie ${lightboxIndex + 1} z ${lightboxImages.length}`}
        />
      )}
    </div>
  );
};

export default ProductTable;
